import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MessageCircle, Flame } from "lucide-react";

export function ForumsPanel({ testid = "forums-panel", spaceToggle = false }) {
  const [space, setSpace] = useState("mtm");
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "" });
  const [active, setActive] = useState(null);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");

  const load = useCallback(() => api.get("/forums/posts", { params: spaceToggle ? { space } : {} })
    .then((r) => setPosts(r.data)), [space, spaceToggle]);
  useEffect(() => { setActive(null); load().catch(() => {}); }, [load]);

  const openPost = async (post) => {
    setActive(post);
    const { data } = await api.get(`/forums/posts/${post.post_id}/comments`);
    setComments(data);
  };

  const submitPost = async (e) => {
    e.preventDefault();
    try {
      await api.post("/forums/posts", form, { params: spaceToggle ? { space } : {} });
      toast.success("Posted");
      setForm({ title: "", content: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    await api.post("/forums/comments", { post_id: active.post_id, content: comment });
    setComment("");
    const { data } = await api.get(`/forums/posts/${active.post_id}/comments`);
    setComments(data);
  };

  return (
    <div className="space-y-8" data-testid={testid}>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Community</div>
          <h1 className="font-serif text-5xl mt-3">{spaceToggle && space === "fenyx" ? "FENYX Q&A" : "Forum"}</h1>
          <p className="text-secondary-luxe text-sm mt-3">
            {spaceToggle && space === "fenyx"
              ? "The members-only question board — self-guided, community-answered."
              : "A quiet space for shared wins and slow questions."}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setOpen(!open)} data-testid="new-post-btn"><Plus size={14}/> New post</button>
      </div>

      {spaceToggle && (
        <div className="flex gap-2" data-testid="forum-space-toggle">
          <button onClick={() => setSpace("mtm")} data-testid="space-mtm"
            className={space === "mtm" ? "btn-primary text-xs" : "btn-secondary text-xs"}>MTM Community</button>
          <button onClick={() => setSpace("fenyx")} data-testid="space-fenyx"
            className={space === "fenyx" ? "btn-primary text-xs" : "btn-secondary text-xs"}>
            <Flame size={12} /> FENYX Community
          </button>
        </div>
      )}

      {open && (
        <form onSubmit={submitPost} className="card-luxe space-y-4 max-w-3xl" data-testid="new-post-form">
          <div><label className="label-luxe">Title</label><input required className="input-luxe" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-title" /></div>
          <div><label className="label-luxe">Body</label><textarea rows={5} required className="input-luxe" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="post-content" /></div>
          <button className="btn-primary" data-testid="post-submit">Publish</button>
        </form>
      )}

      {active ? (
        <div className="space-y-6">
          <button className="btn-ghost" onClick={() => setActive(null)} data-testid="back-to-forum">← Back</button>
          <article className="card-luxe">
            <div className="overline">{active.author_name}</div>
            <h2 className="font-serif text-3xl mt-3">{active.title}</h2>
            <p className="mt-4 text-secondary-luxe whitespace-pre-wrap">{active.content}</p>
          </article>
          <section className="space-y-3">
            <div className="overline">Replies</div>
            {comments.map((c) => (
              <div key={c.comment_id} className="card-luxe">
                <div className="text-xs text-accent">{c.author_name}</div>
                <div className="text-sm mt-2 whitespace-pre-wrap">{c.content}</div>
              </div>
            ))}
            <form onSubmit={submitComment} className="card-luxe flex gap-2">
              <input className="input-luxe flex-1" placeholder="Add a reply" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="comment-input" />
              <button className="btn-primary" data-testid="comment-submit">Reply</button>
            </form>
          </section>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => (
            <button key={p.post_id} onClick={() => openPost(p)} className="card-luxe w-full text-left block hover:border-luxe-strong" data-testid={`post-${p.post_id}`}>
              <div className="flex items-center justify-between">
                <div className="overline">{p.author_name}</div>
                <div className="text-xs text-tertiary-luxe flex items-center gap-1"><MessageCircle size={12} />{p.comment_count}</div>
              </div>
              <h3 className="font-serif text-2xl mt-2">{p.title}</h3>
              <p className="text-secondary-luxe text-sm mt-2 line-clamp-2">{p.content}</p>
            </button>
          ))}
          {posts.length === 0 && <div className="text-secondary-luxe">No posts yet — be the first.</div>}
        </div>
      )}
    </div>
  );
}
